/** Display-safe failure details. Provider payloads belong only in backend diagnostics. */
export interface PublicFailure {
  code: string
  message: string
  retryable: boolean
}

export interface ApiErrorBody {
  code?: string
  message?: string
  /** Legacy response name, retained during coordinated deployments. */
  error?: string
  requestId?: string
  fieldErrors?: Record<string, string>
}

export function httpFailure(status: number): PublicFailure {
  switch (status) {
    case 401:
      return {
        code: 'SESSION_EXPIRED',
        message: 'Your session has expired. Sign in again to continue.',
        retryable: false
      }
    case 403:
      return {
        code: 'ACCESS_DENIED',
        message:
          'Your account does not have permission to perform this action. Contact an administrator if you need access.',
        retryable: false
      }
    case 404:
      return { code: 'NOT_FOUND', message: 'This item could not be found. It may have been deleted.', retryable: false }
    case 409:
      return {
        code: 'CONFLICT',
        message: 'This action conflicts with a recent change. Refresh the item before trying again.',
        retryable: false
      }
    case 429:
      return { code: 'RATE_LIMITED', message: 'Too many requests. Wait a moment before trying again.', retryable: true }
    case 400:
    case 422:
      return {
        code: 'INVALID_INPUT',
        message: 'The request could not be accepted. Review your inputs and try again.',
        retryable: false
      }
    default:
      return {
        code: 'SERVICE_UNAVAILABLE',
        message: 'The service could not complete this request. Try again in a moment.',
        retryable: true
      }
  }
}

/** Recognizes known legacy worker failures without ever echoing a raw provider message. */
export function generationFailure(errorMessage?: string, status?: string): PublicFailure {
  const reason = errorMessage ?? ''
  if (status === 'moderation_failed' || /safety|moderation|content.policy|blocked.prompt/i.test(reason)) {
    return {
      code: 'CONTENT_REJECTED',
      message:
        'The provider blocked this generation under its content policy. Adjust the prompt or reference images before trying again.',
      retryable: false
    }
  }
  // Status codes match only as whole numbers, never as digits inside a stored ID.
  if (/\b429\b|RESOURCE_EXHAUSTED|quota|rate.limit/i.test(reason)) {
    return {
      code: 'PROVIDER_LIMIT_REACHED',
      message: 'The generation provider has reached its request limit. Try again later.',
      retryable: true
    }
  }
  if (/MAX_TOKENS|output token limit/i.test(reason)) {
    return {
      code: 'OUTPUT_LIMIT_REACHED',
      message:
        'The provider reached its output limit before creating an image. Simplify the prompt or try another generation.',
      retryable: true
    }
  }
  if (/timed?.?out|timeout|deadline|did not finish within/i.test(reason)) {
    return {
      code: 'GENERATION_TIMEOUT',
      message:
        'The generation did not finish within the allowed time. Review the run before starting another generation.',
      retryable: true
    }
  }
  if (/no (image|candidates|parts)|image.*not.*(return|found)|empty.response/i.test(reason)) {
    return {
      code: 'NO_IMAGE_RETURNED',
      message: 'The provider returned no image. Adjust the prompt or try another generation.',
      retryable: true
    }
  }
  if (/\b503\b|overload|unavailable/i.test(reason)) {
    return {
      code: 'PROVIDER_UNAVAILABLE',
      message: 'The generation provider is temporarily unavailable. Try again later.',
      retryable: true
    }
  }
  if (/INVALID_ARGUMENT|invalid (input|argument|image)|unsupported/i.test(reason)) {
    return {
      code: 'INVALID_GENERATION_INPUT',
      message:
        'The provider could not use the generation inputs. Review the settings and reference images before trying again.',
      retryable: false
    }
  }
  return {
    code: 'GENERATION_FAILED',
    message:
      'The generation could not be completed. Review its settings and try again. Share the generation reference if the problem continues.',
    retryable: true
  }
}

export function getGenerationFailure(record: {
  failure?: PublicFailure
  errorMessage?: string
  status?: string
}): PublicFailure {
  return record.failure ?? generationFailure(record.errorMessage, record.status)
}

/** Additive API projection; old records need no backfill and retain their stored diagnostics. */
export function withGenerationFailure<T extends { status: string; errorMessage?: string; failure?: PublicFailure }>(
  record: T
): T & { failure?: PublicFailure } {
  if (record.status !== 'error' && record.status !== 'moderation_failed') return record
  const failure = getGenerationFailure(record)
  return { ...record, failure, errorMessage: failure.message }
}
